import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Mail, Shield, Eye, EyeOff, Upload, Trash2, Check, X, Loader2 } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { SectionCard, Avatar } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: 'Yönetici',
  proje_yoneticisi: 'Proje Yöneticisi',
  takim_lideri: 'Takım Lideri',
  takim_uyesi: 'Takım Üyesi',
  project_manager: 'Proje Yöneticisi',
  team_leader: 'Takım Lideri',
  team_member: 'Takım Üyesi',
};

export default function ProfileSettings() {
  const { user, setUser, checkAuth } = useContext(AuthContext);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [name, setName] = useState(user?.name || '');
  const [editingName, setEditingName] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [confirmDeletePic, setConfirmDeletePic] = useState(false);

  usePageHeader([{ label: 'Profil Ayarları' }], null, []);

  const handleNameUpdate = async () => {
    if (!name.trim()) {
      toast.error('Ad soyad boş bırakılamaz');
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API}/users/${user.id}`, { name: name.trim() });
      setUser({ ...user, name: name.trim() });
      setEditingName(false);
      toast.success('Ad soyad başarıyla güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ad soyad güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const cancelNameEdit = () => {
    setName(user.name);
    setEditingName(false);
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Sadece resim dosyaları yüklenebilir (JPEG, PNG, GIF, WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resim boyutu 5MB'dan küçük olmalıdır");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/users/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUser({ ...user, picture: response.data.picture });
      toast.success('Profil resminiz güncellendi');
      setPreviewImage(null);
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resim yüklenemedi');
      setPreviewImage(null);
    } finally {
      setUploadingPicture(false);
      e.target.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    setUploadingPicture(true);
    try {
      await axios.delete(`${API}/users/profile-picture`);
      const updated = { ...user };
      delete updated.picture;
      setUser(updated);
      toast.success('Profil resmi silindi');
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resim silinemedi');
    } finally {
      setUploadingPicture(false);
      setConfirmDeletePic(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      return;
    }
    if (!/[A-Z]/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 büyük harf içermelidir');
      return;
    }
    if (!/[a-z]/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 küçük harf içermelidir');
      return;
    }
    if (!/\d/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 rakam içermelidir');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success('Şifreniz başarıyla değiştirildi');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  const pictureUrl = previewImage
    || (user.picture && (user.picture.startsWith('http') ? user.picture : `${BACKEND_URL}${user.picture}`));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Profil Bilgileri" subtitle="Hesap bilgilerinizi görüntüleyin ve düzenleyin">
          <div className="space-y-4">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {pictureUrl ? (
                  <img
                    src={pictureUrl}
                    alt={user.name}
                    className="rounded-full object-cover"
                    style={{ width: 72, height: 72, border: '1px solid var(--border)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Avatar u={user} size={72} />
                )}
                {uploadingPicture && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <Loader2 size={18} className="animate-spin text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  id="profile-picture-upload"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleProfilePictureUpload}
                  disabled={uploadingPicture}
                  className="hidden"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="ox-btn"
                    disabled={uploadingPicture}
                    onClick={() => document.getElementById('profile-picture-upload').click()}
                  >
                    <Upload size={13} style={{ marginRight: 6 }} />
                    {uploadingPicture ? 'Yükleniyor...' : 'Resim Yükle'}
                  </button>
                  {user.picture && !user.picture.includes('googleusercontent.com') && (
                    <button
                      type="button"
                      className="ox-btn ox-btn-ghost"
                      onClick={() => setConfirmDeletePic(true)}
                      disabled={uploadingPicture}
                      style={{ color: 'var(--danger)' }}
                    >
                      <Trash2 size={13} style={{ marginRight: 4 }} />
                      Kaldır
                    </button>
                  )}
                </div>
                <div className="text-[11px] ox-text-muted mt-2">JPEG, PNG, GIF veya WEBP. Max 5MB.</div>
              </div>
            </div>

            <div className="ox-field">
              <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">Ad Soyad</label>
              {editingName ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    className="ox-input flex-1"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ad Soyad"
                  />
                  <button className="ox-btn ox-btn-primary" onClick={handleNameUpdate} disabled={loading}>
                    <Check size={13} />
                  </button>
                  <button className="ox-btn ox-btn-ghost" onClick={cancelNameEdit} disabled={loading}>
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between ox-surface-muted rounded-md px-3 py-2 mt-1">
                  <div className="flex items-center gap-2">
                    <User size={14} style={{ color: 'var(--text-muted)' }} />
                    <span className="text-sm">{user.name}</span>
                  </div>
                  <button className="ox-btn ox-btn-ghost text-xs" onClick={() => setEditingName(true)}>Düzenle</button>
                </div>
              )}
            </div>

            <div className="ox-field">
              <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">E-posta</label>
              <div className="flex items-center gap-2 ox-surface-muted rounded-md px-3 py-2 mt-1">
                <Mail size={14} style={{ color: 'var(--text-muted)' }} />
                <span className="text-sm">{user.email}</span>
              </div>
            </div>

            <div className="ox-field">
              <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">Rol</label>
              <div className="flex items-center gap-2 ox-surface-muted rounded-md px-3 py-2 mt-1">
                <Shield size={14} style={{ color: 'var(--text-muted)' }} />
                <span className="text-sm">{ROLE_LABELS[user.role] || user.role}</span>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Şifre Değiştir" subtitle="Hesap güvenliğiniz için düzenli olarak değiştirin">
          <form onSubmit={handleChangePassword} className="space-y-3">
            <PasswordField
              label="Mevcut Şifre"
              value={passwordData.currentPassword}
              onChange={(v) => setPasswordData({ ...passwordData, currentPassword: v })}
              show={showCurrent}
              onToggle={() => setShowCurrent(!showCurrent)}
              disabled={loading}
            />
            <div>
              <PasswordField
                label="Yeni Şifre"
                value={passwordData.newPassword}
                onChange={(v) => setPasswordData({ ...passwordData, newPassword: v })}
                show={showNew}
                onToggle={() => setShowNew(!showNew)}
                disabled={loading}
              />
              <div className="text-[11px] ox-text-muted mt-1">Min. 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam</div>
            </div>
            <PasswordField
              label="Yeni Şifre (Tekrar)"
              value={passwordData.confirmPassword}
              onChange={(v) => setPasswordData({ ...passwordData, confirmPassword: v })}
              show={showConfirm}
              onToggle={() => setShowConfirm(!showConfirm)}
              disabled={loading}
            />
            <button type="submit" className="ox-btn ox-btn-primary w-full" disabled={loading}>
              <Lock size={13} style={{ marginRight: 6 }} />
              {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
            </button>
          </form>
        </SectionCard>
      </div>

      <SectionCard title="Güvenlik İpuçları">
        <div className="grid md:grid-cols-3 gap-4">
          <Tip
            icon={<Check size={14} />}
            color="var(--good)"
            title="Güçlü Şifre Kullanın"
            text="Min. 8 karakter, büyük/küçük harf ve rakam içermeli."
          />
          <Tip
            icon={<Shield size={14} />}
            color="var(--warn)"
            title="Şifrenizi Paylaşmayın"
            text="Kimseyle şifrenizi paylaşmayın, kayıt etmeyin."
          />
          <Tip
            icon={<Lock size={14} />}
            color="var(--accent)"
            title="Düzenli Değiştirin"
            text="Şifrenizi düzenli aralıklarla güncelleyin."
          />
        </div>
      </SectionCard>

      {confirmDeletePic && (
        <div className="ox-modal-backdrop" onClick={() => setConfirmDeletePic(false)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold">Profil resmini sil</div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setConfirmDeletePic(false)}><X size={14} /></button>
            </div>
            <div className="ox-modal-body">
              <div className="text-sm ox-text-muted">Profil resminizi silmek istediğinizden emin misiniz?</div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setConfirmDeletePic(false)}>İptal</button>
              <button className="ox-btn ox-btn-danger" onClick={handleDeleteProfilePicture} disabled={uploadingPicture}>
                {uploadingPicture ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, disabled }) {
  return (
    <div className="ox-field">
      <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">{label}</label>
      <div className="relative mt-1">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required
          className="ox-input w-full pr-9"
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className="absolute top-1/2 -translate-y-1/2 right-2 ox-text-muted hover:text-[color:var(--text)]"
          style={{ background: 'none', border: 'none' }}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

function Tip({ icon, color, title, text }) {
  return (
    <div className="flex items-start gap-3 p-3 ox-surface-muted rounded-md">
      <div
        className="flex items-center justify-center rounded-md flex-shrink-0"
        style={{ width: 26, height: 26, background: color, color: '#fff' }}
      >
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-[11.5px] ox-text-muted mt-0.5">{text}</div>
      </div>
    </div>
  );
}
